import { useState } from 'react';
import {
  useNpsLinksQuery,
  useNpsResultsQuery,
  useRotateNpsLink,
  type Audience,
} from '../api/nps';
import { showToast } from '../components/Toast';
import NpsScoreTile from '../components/nps/NpsScoreTile';
import ShareLinkCard from '../components/nps/ShareLinkCard';

const MIN_RESPONSES = 5;

/**
 * A single location's NPS: its own scores by audience, its recent comments, and
 * the three shareable links (with QR codes) it hands out to caregivers,
 * clients, and referral sources. No map — that's the admin view.
 */
export default function NpsFranchisee({ af }: { af: string }) {
  const links = useNpsLinksQuery(af);
  const results = useNpsResultsQuery(af);
  const rotate = useRotateNpsLink(af);
  const [rotatingAudience, setRotatingAudience] = useState<Audience | null>(null);

  function handleRotate(audience: Audience) {
    setRotatingAudience(audience);
    rotate.mutate(audience, {
      onSuccess: () => showToast('New link generated — the old one no longer works', { variant: 'success' }),
      onError: (e) => showToast((e as Error).message, { variant: 'error' }),
      onSettled: () => setRotatingAudience(null),
    });
  }

  return (
    <div className="nps-page">
      <div className="ds-section-header">
        <h2>Net Promoter Score</h2>
        <span className="meta">Anonymous feedback from your caregivers, clients, and referral sources</span>
      </div>

      {/* Results */}
      {results.isLoading && <div className="nps-respond-muted">Loading results…</div>}
      {results.error && (
        <div className="nps-respond-error">Couldn’t load results: {(results.error as Error).message}</div>
      )}
      {results.data && (
        <>
          <div className="nps-tile-grid">
            <NpsScoreTile title="Overall" summary={results.data.overall} minResponses={MIN_RESPONSES} />
            {results.data.byAudience.map((a) => (
              <NpsScoreTile key={a.audience} title={a.label} summary={a} minResponses={MIN_RESPONSES} />
            ))}
          </div>

          {results.data.comments.length > 0 && (
            <div className="nps-comments">
              <h3>Recent comments</h3>
              <ul>
                {results.data.comments.map((c, i) => (
                  <li key={i} className={`nps-comment ${c.bucket}`}>
                    <span className="nps-comment-score">{c.score}</span>
                    <div>
                      <p>{c.comment}</p>
                      <span className="nps-comment-meta">{c.label}</span>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}

      {/* Share links */}
      <div className="ds-section-header" style={{ marginTop: 28 }}>
        <h2>Share your survey links</h2>
        <span className="meta">One link per audience. Share the link or the QR code — responses are anonymous.</span>
      </div>
      {links.isLoading && <div className="nps-respond-muted">Loading links…</div>}
      {links.error && (
        <div className="nps-respond-error">Couldn’t load links: {(links.error as Error).message}</div>
      )}
      {links.data && (
        <div className="nps-share-grid">
          {links.data.links.map((l) => (
            <ShareLinkCard
              key={l.audience}
              link={l}
              rotating={rotatingAudience === l.audience}
              onRotate={() => handleRotate(l.audience)}
            />
          ))}
        </div>
      )}
    </div>
  );
}
